// Added by Sejune Oh on 2019-07-05
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef, RefObject } from 'react'
import { action, observable, computed, makeObservable } from 'mobx'
import { observer, inject } from 'mobx-react'
import localeCode from 'locale-code'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import {
  UILanguageProps,
  ReactSelectEvent,
  TextFormatter,
  InputBlurValidator,
  MgmtValidator,
  ReactFormEvent,
  FormatterProps,
  ReactEmptyEvent,
} from '@/helper/define'
import { getFormData } from '@/helper/util'
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import SystemAdminAdd from './SystemAdminAdd'
import withUserValidator from '../UserValidator'
import { UserAuthStore } from '@/stores/user/UserAuth'
import { UserElementProps } from '@/stores/mgmt/user'
import ProductStore from '@/stores/ProductStore'
import EventStore from '@/stores/EventStore'
import { SystemConfigStore } from '@/stores/mgmt/config'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface SystemAdminAddCProps extends MgmtValidator, FormatterProps {
  emailRef: RefObject<any>
  pwdRef: RefObject<any>
  confirmPwdRef: RefObject<any>
  userAuth?: UserAuthStore
  product?: ProductStore
  globalEvt?: EventStore
  systemConfig?: SystemConfigStore
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('userAuth', 'product', 'globalEvt', 'systemConfig')
@observer
class SystemAdminAddContainer extends Component<SystemAdminAddCProps> {
  uiLanguage?: UILanguageProps
  mounted: boolean = false
  isErrorOccured: boolean = false
  errMessage: string = ''

  _uiLanguageList: UILanguageProps[] = []
  _formSubmitBtnRef = createRef<HTMLButtonElement>()
  _formId = 'u-f'

  constructor(props: SystemAdminAddCProps) {
    super(props)
    makeObservable(this, {
      uiLanguage: observable,
      mounted: observable,
      isErrorOccured: observable,
      errMessage: observable,
      componentDidMount: action,
      _setUILanguage: action,
      _handleSnackbarClose: action,
      _handleAddSysmtemAdmin: action,
      userAuth: computed,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  componentDidMount = async () => {
    await this.props.systemConfig!.initialize()
    this._uiLanguageList = this.props.systemConfig!.systemConfigsOption.languageConfig!.map((item) => {
      const nativeName = localeCode.getLanguageNativeName(item.lang_code)
      const countryCode = localeCode.getCountryCode(item.lang_code)
      const langCode = localeCode.getLanguageCode(item.lang_code)

      return {
        id: item.lang_code,
        label: `${nativeName} (${langCode}-${countryCode})`,
      }
    })

    /** set default language - english */
    this.uiLanguage = this._uiLanguageList.filter((lang) => lang.id === 'en-US')[0]
    this.mounted = true
  }

  render() {
    const { validator, blurValidator: baseBlurValidator, ...others } = this.props
    const blurValidator = {
      ...baseBlurValidator,
      userNameBlurValidator: this._userNameBlurValidator,
      emailBlurValidator: this._emailBlurValidator,
    }
    const formatter = {
      uiLanguageFormatter: this._uiLanguageFormatter,
    }

    return (
      this.mounted && (
        <SystemAdminAdd
          {...others}
          validator={validator}
          blurValidator={blurValidator}
          formatter={formatter}
          uiLanguage={this.uiLanguage!}
          onUILanguageChange={this._handleUILanguageChange}
          uiLanguageList={this._uiLanguageList}
          formSubmitBtnRef={this._formSubmitBtnRef}
          formId={this._formId}
          onSubmit={this._handleSubmit}
          isErrorOccured={this.isErrorOccured}
          onSnackbarClose={this._handleSnackbarClose}
          errMessage={this.errMessage}
          onConfirmBtnClick={this._handleAddSysmtemAdmin}
          onRequestGoing={this.props.globalEvt!.isOnLoading!}
        />
      )
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setUILanguage = (uiLang: UILanguageProps) => {
    this.uiLanguage = uiLang
  }

  _handleSnackbarClose: ReactEmptyEvent = () => {
    this.isErrorOccured = false
  }

  _handleAddSysmtemAdmin: ReactEmptyEvent = async () => {
    try {
      this.props.globalEvt!.setLoadingOn()

      const form = document.getElementById(this._formId) as HTMLFormElement
      const sysAdminElement = getFormData<UserElementProps>(form)!

      await this.userAuth.signUpSystemAdminUser(sysAdminElement)
      Logger.devLog('System admin user is created!')


      const {authToken} = await this.userAuth.checkUserInfo({ email: sysAdminElement.email, password: sysAdminElement.password })
      await this.userAuth.signIn(authToken)

      this.props.product!.createSysAdmin()
    } catch (err) {
      Logger.errLog('Fail to add system admin')
      console.log(err)

      this.errMessage = err.response ? err.response!.data.message : err.message
      this.isErrorOccured = true
    } finally {
      this.props.globalEvt!.setLoadingOff()
    }
  }

  get userAuth() {
    return this.props.userAuth!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleSubmit: ReactFormEvent = (e) => {
    e.preventDefault()
  }

  _handleUILanguageChange: ReactSelectEvent = ({ target }) => {
    const langId = target.value
    const selectedLangId = this._uiLanguageList.find(({ id }) => id === langId)
    this._setUILanguage(selectedLangId!)
  }

  _uiLanguageFormatter: TextFormatter<UILanguageProps> = (item: UILanguageProps) => {
    return `${item.label}`
  }

  _userNameBlurValidator: InputBlurValidator = async (value) => {
    return [false]
  }

  _emailBlurValidator: InputBlurValidator = async (value) => {
    return [false]
  }
}

export default withUserValidator()(SystemAdminAddContainer)

